/**
 * ERC Different Unit Net Validator Adapter for DRC System
 *
 * This validator checks for multi-unit components where corresponding pins are on different nets.
 *
 * Feature-gated via DrcEnabledCheckers.ErcDifferentUnitNetValidator
 */
import { createTUID, EditorModes, ReviewType } from "@buildwithflux/models";
import { getProblemTypeKeyFromReviewType } from "../../drc/types";
/**
 * Validator that checks for different nets in multi-unit component pins.
 */
export class ErcDifferentUnitNetValidator {
    reviewType = ReviewType.DesignRuleCheckErcDifferentUnitNet;
    problemTypeKey = getProblemTypeKeyFromReviewType(this.reviewType);
    defaultHidden = true;
    async checkForProblems(inputs) {
        const foundProblems = [];
        // Build terminal to net mapping
        const terminalToNet = {};
        for (const [netHash, terminals] of Object.entries(inputs.netMap)) {
            for (const terminalId of Object.keys(terminals)) {
                terminalToNet[terminalId] = netHash;
            }
        }
        // Group elements by base reference designator
        const componentGroups = {};
        for (const [elementUid, element] of Object.entries(inputs.elements)) {
            const designator = element.label ?? "";
            if (!designator)
                continue;
            const baseRef = designator.replace(/[A-Z]$/, "");
            if (baseRef === designator)
                continue;
            if (!componentGroups[baseRef]) {
                componentGroups[baseRef] = { elementUids: [], pinNets: {} };
            }
            componentGroups[baseRef].elementUids.push(elementUid);
            const terminals = element.part_version_data_cache?.terminals;
            if (!terminals)
                continue;
            for (const [terminalUid, terminal] of Object.entries(terminals)) {
                const pinName = terminal.name ?? terminal.pin_number ?? "";
                if (!pinName)
                    continue;
                const terminalId = createTUID([elementUid, terminalUid]);
                const netHash = terminalToNet[terminalId] ?? "unconnected";
                if (!componentGroups[baseRef].pinNets[pinName]) {
                    componentGroups[baseRef].pinNets[pinName] = new Set();
                }
                componentGroups[baseRef].pinNets[pinName].add(netHash);
            }
        }
        // Report components with pins on different nets
        for (const [baseRef, data] of Object.entries(componentGroups)) {
            for (const [pinName, nets] of Object.entries(data.pinNets)) {
                if (nets.size > 1) {
                    foundProblems.push({
                        problemTypeKey: this.problemTypeKey,
                        key: `${this.problemTypeKey}_${baseRef}_${pinName}`,
                        label: `Multi-unit component ${baseRef}:${pinName} is on different nets`,
                        affectedItems: data.elementUids.map((uid) => ({ type: "element", uid })),
                        affectedViews: [EditorModes.schematic],
                    });
                }
            }
        }
        return {
            error: false,
            problemTypeKey: this.problemTypeKey,
            foundProblems,
        };
    }
}
//# sourceMappingURL=ErcDifferentUnitNetValidator.js.map